import React, { useState, useEffect } from 'react';
import siteSettingsService from '../../services/siteSettingsService';

const AdminControls = ({ isProtected, setIsProtected }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState('success'); // 'success' or 'error'
  const [useImmediatePaymentProcessing, setUseImmediatePaymentProcessing] = useState(false);
  
  // Fetch payment processing setting on component mount
  useEffect(() => {
    const fetchPaymentProcessingSetting = async () => {
      try {
        const settings = await siteSettingsService.getSettings();
        setUseImmediatePaymentProcessing(settings.useImmediatePaymentProcessing || false);
      } catch (error) {
        console.error('Error fetching payment processing setting:', error);
      }
    };
    
    fetchPaymentProcessingSetting();
  }, []);
  
  const handleToggleProtection = async () => {
    setIsUpdating(true);
    
    try {
      // Call the setIsProtected function from App.js which updates Firestore
      await setIsProtected(!isProtected);
      
      // If we're removing protection, we should also clear the session storage
      if (isProtected) {
        sessionStorage.removeItem('authenticated');
      }
      
      // Show success message
      setStatusType('success');
      setStatusMessage(
        isProtected 
          ? 'Password protection disabled. Site is now public for all users.' 
          : 'Password protection enabled. All users will now need a password to access the site.'
      );
    } catch (error) {
      // Show error message
      setStatusType('error');
      setStatusMessage(`Error updating protection status: ${error.message}`);
    } finally {
      setIsUpdating(false);
      
      // Clear the status message after 5 seconds
      setTimeout(() => {
        setStatusMessage('');
      }, 5000);
    }
  };

  const handleTogglePaymentProcessing = async () => {
    setIsUpdating(true);
    
    try {
      // Update the payment processing mode in Firestore
      const success = await siteSettingsService.updatePaymentProcessingMode(!useImmediatePaymentProcessing);
      
      if (success) {
        // Update local state
        setUseImmediatePaymentProcessing(!useImmediatePaymentProcessing);
        
        // Show success message
        setStatusType('success');
        setStatusMessage(
          useImmediatePaymentProcessing 
            ? 'Payment processing mode changed to Authorization (payments will be held before confirming).' 
            : 'Payment processing mode changed to Immediate (payments will be processed immediately).'
        );
      } else {
        throw new Error('Failed to update payment processing mode');
      }
    } catch (error) {
      // Show error message
      setStatusType('error');
      setStatusMessage(`Error updating payment processing mode: ${error.message}`);
    } finally {
      setIsUpdating(false);
      
      // Clear the status message after 5 seconds
      setTimeout(() => {
        setStatusMessage('');
      }, 5000);
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-4">Admin Controls</h2>
      
      {/* Site Protection Section */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-2">Site Protection Status</h3>
        <div className="flex items-center space-x-2 mb-4">
          <span className="text-sm">
            {isProtected ? 'Password Protected' : 'Public'}
          </span>
          <span 
            className={`inline-block w-3 h-3 rounded-full ${
              isProtected ? 'bg-red-500' : 'bg-green-500'
            }`}
          ></span>
        </div>
        
        <button
          onClick={handleToggleProtection}
          disabled={isUpdating}
          className={`px-4 py-2 text-white rounded transition-colors ${
            isUpdating 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {isUpdating 
            ? 'Updating...' 
            : isProtected 
              ? 'Remove Password Protection' 
              : 'Enable Password Protection'
          }
        </button>
        
        <div className="mt-4 text-sm text-gray-600">
          <p>
            {isProtected 
              ? 'Current password: dentalhero2025' 
              : 'Site is currently public and accessible to everyone.'}
          </p>
          <p className="mt-2">
            Note: Changes to protection status will take effect immediately for all users.
          </p>
        </div>
      </div>
      
      {/* Payment Processing Section */}
      <div className="pt-6 border-t border-gray-200">
        <h3 className="text-lg font-semibold mb-2">Payment Processing Mode</h3>
        <div className="flex items-center space-x-2 mb-4">
          <span className="text-sm">
            {useImmediatePaymentProcessing ? 'Immediate Processing' : 'Authorization (Hold Payments)'}
          </span>
          <span 
            className={`inline-block w-3 h-3 rounded-full ${
              useImmediatePaymentProcessing ? 'bg-green-500' : 'bg-blue-500'
            }`}
          ></span>
        </div>
        
        <button
          onClick={handleTogglePaymentProcessing}
          disabled={isUpdating}
          className={`px-4 py-2 text-white rounded transition-colors ${
            isUpdating 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {isUpdating 
            ? 'Updating...' 
            : useImmediatePaymentProcessing 
              ? 'Switch to Authorization Mode' 
              : 'Switch to Immediate Processing'
          }
        </button>
        
        <div className="mt-4 text-sm text-gray-600">
          <p>
            {useImmediatePaymentProcessing 
              ? 'Payments are processed immediately when a patient books an appointment.' 
              : 'Payments are authorized (held) when a patient books an appointment and only captured when the appointment is confirmed.'}
          </p>
          <p className="mt-2">
            Note: Changes to payment processing mode will take effect for all new appointments.
          </p>
        </div>
      </div>
      
      {/* Status Message */}
      {statusMessage && (
        <div className={`mt-6 p-3 rounded ${
          statusType === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {statusMessage}
        </div>
      )}
    </div>
  );
};

export default AdminControls;
